// src/components/DriverOriginal.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import { FaSearch, FaPlus, FaSyncAlt, FaIdCard } from "react-icons/fa";
import { FaTrashAlt, FaSave, FaTimes } from "react-icons/fa";
import "../styles/DriverOriginal.css";
import { useTranslation } from "react-i18next";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const ISO2_RE = /^[A-Za-z]{2}$/; // country (ISO-2)

// ENUM do backend
const EF_OPTIONS = ["MAE", "PAI", "ESPOSA", "ESPOSO", "FILHO", "OUTRO"];
const EF_LABELS = {
  MAE: "mãe",
  PAI: "pai",
  ESPOSA: "esposa",
  ESPOSO: "esposo",
  FILHO: "filho",
  OUTRO: "outro",
};

// Dias da semana (códigos do backend)
const WA_DAYS = [
  { code: "MON", label: "Seg" },
  { code: "TUE", label: "Ter" },
  { code: "WED", label: "Qua" },
  { code: "THU", label: "Qui" },
  { code: "FRI", label: "Sex" },
  { code: "SAT", label: "Sáb" },
  { code: "SUN", label: "Dom" },
];

// ADR/HazMat options
const ADR_OPTIONS = ["SIM", "NAO"];
const ADR_LABELS = { SIM: "SIM", NAO: "NÃO" };

// HIAB/Grúa options
const HIAB_OPTIONS = ["SIM", "NAO"];
const HIAB_LABELS = { SIM: "SIM", NAO: "NÃO" };

// Tanker (cisterna) options
const TANKER_OPTIONS = ["SIM", "NAO"];
const TANKER_LABELS = { SIM: "SIM", NAO: "NÃO" };

// Refrigerated (frigorífico) options
const REFRIG_OPTIONS = ["SIM", "NAO"];
const REFRIG_LABELS = { SIM: "SIM", NAO: "NÃO" };

// Special transport (excecionais/indivisíveis) options
const SPECIAL_OPTIONS = ["SIM", "NAO"];
const SPECIAL_LABELS = { SIM: "SIM", NAO: "NÃO" };

// Night & weekend driving
const NIGHTW_OPTIONS = ["SIM", "NAO"];
const NIGHTW_LABELS = { SIM: "SIM", NAO: "NÃO" };

// Sleep in truck
const SLEEP_OPTIONS = ["SIM", "NAO"];
const SLEEP_LABELS = { SIM: "SIM", NAO: "NÃO" };

// Urgent jobs
const URGENT_OPTIONS = ["SIM", "NAO"];
const URGENT_LABELS = { SIM: "SIM", NAO: "NÃO" };

// NOVO: Take photos (permite tirar fotos de documentos, cargas, etc.)
const TAKE_PHOTOS_OPTIONS = ["SIM", "NAO"];
const TAKE_PHOTOS_LABELS = { SIM: "SIM", NAO: "NÃO" };

// NOVO: GPS Sharing
const GPS_SHARING_OPTIONS = ["SIM", "NAO"];
const GPS_SHARING_LABELS = { SIM: "SIM", NAO: "NÃO" };

// Weight unit
const WEIGHT_OPTIONS = ["KG", "LB"];
const WEIGHT_LABELS = { KG: "kg", LB: "lb" };

// Temperature unit (novo campo)
const TEMPERATURE_OPTIONS = ["Cº", "Fº"];
const TEMPERATURE_LABELS = { "Cº": "Cº", "Fº": "Fº" };

// Contact preferences (novo campo)
const CONTACT_PREF_OPTIONS = ["ANY", "CALL_OK", "URGENT_ONLY", "NO_CALLS", "WHATSAPP_ONLY", "SMS_ONLY", "EMAIL_ONLY"];
const CONTACT_PREF_LABELS = {
  ANY: "Qualquer",
  CALL_OK: "Pode ligar",
  URGENT_ONLY: "Só urgentes",
  NO_CALLS: "Não ligar",
  WHATSAPP_ONLY: "WhatsApp",
  SMS_ONLY: "SMS",
  EMAIL_ONLY: "Email",
};

// Work eligibility (alinhado com backend ENUM)
const WORK_ELIGIBILITY_OPTIONS = ["PT", "EU_EEA", "WORK_PERMIT", "NONE"];
const WORK_ELIGIBILITY_LABELS = {
  PT: "Portugal",
  EU_EEA: "EU/EEE",
  WORK_PERMIT: "Work Permit",
  NONE: "None",
};

// ---- Helpers locais para license_categories (frontend) ----
function parseLicenseCategoriesInput(input) {
  if (input == null) return null; // não enviado
  let arr = [];
  if (Array.isArray(input)) arr = input;
  else if (typeof input === "string") arr = input.split(/[,;|\s]+/);
  else return "__INVALID__";
  const norm = arr
    .map((v) => String(v || "").trim().toUpperCase())
    .filter((v) => v.length > 0);
  const VALID_RE = /^[A-Z0-9+]+$/; // ex.: B, C1E, C+E
  for (const c of norm) if (!VALID_RE.test(c)) return "__INVALID__";
  return [...new Set(norm)];
}

// ---- Helpers locais para languages_spoken (frontend) ----
function parseLanguagesInput(input) {
  if (input == null) return null; // não enviado
  let arr = [];
  if (Array.isArray(input)) arr = input;
  else if (typeof input === "string") arr = input.split(/[,;|\s]+/);
  else return "__INVALID__";
  const norm = arr
    .map((v) => String(v || "").trim().toLowerCase())
    .filter((v) => v.length > 0);
  const ISO639_1 = /^[a-z]{2}$/; // ex.: pt, en, es
  for (const c of norm) if (!ISO639_1.test(c)) return "__INVALID__";
  return [...new Set(norm)];
}

// toggle genérica para chips de dias
function toggleDay(_arr, setArr, code) {
  setArr((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
}

function DriverOriginal() {
  const { t, ready } = useTranslation("driveroriginal");

  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // create-inline state
  const [isCreating, setIsCreating] = useState(false);
  const [createFullname, setCreateFullname] = useState("");
  const [createNickname, setCreateNickname] = useState("");
  const [createPhone, setCreatePhone] = useState("");
  const [createEmergencyName, setCreateEmergencyName] = useState("");
  const [createEmergencyContact, setCreateEmergencyContact] = useState("");
  const [createEmergencyFamily, setCreateEmergencyFamily] = useState("");
  const [createHomecity, setCreateHomecity] = useState("");
  const [createCity, setCreateCity] = useState("");
  const [createCountry, setCreateCountry] = useState("");
  const [createLicenseCountry, setCreateLicenseCountry] = useState("");
  const [createAddress, setCreateAddress] = useState("");
  const [createPostalCode, setCreatePostalCode] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createBirthDate, setCreateBirthDate] = useState("");
  const [createTaxId, setCreateTaxId] = useState("");
  const [createTaxIdExpiry, setCreateTaxIdExpiry] = useState("");
  const [createDrivingLicenseNumber, setCreateDrivingLicenseNumber] = useState("");
  const [createLicenseCategories, setCreateLicenseCategories] = useState("");
  const [createAcceptterms, setCreateAcceptterms] = useState(false);
  const [createError, setCreateError] = useState("");

  // fotos
  const [createDLPhoto1, setCreateDLPhoto1] = useState("");
  const [createDLPhoto2, setCreateDLPhoto2] = useState("");
  const [createTachoPhoto1, setCreateTachoPhoto1] = useState("");
  const [createTachoPhoto2, setCreateTachoPhoto2] = useState("");

  // datas do cartão tacógrafo
  const [createIssueDateTachoCard, setCreateIssueDateTachoCard] = useState("");
  const [createExpiryDateTachoCard, setCreateExpiryDateTachoCard] = useState("");

  // elegibilidade (ENUM)
  const [createWorkEligibility, setCreateWorkEligibility] = useState("");

  // ADR
  const [createAdrHazmarCertification, setCreateAdrHazmarCertification] = useState("NAO");
  const [createExpiryDateAdrHazmatCertificate, setCreateExpiryDateAdrHazmatCertificate] = useState("");
  const [createAdrCertPhoto1, setCreateAdrCertPhoto1] = useState("");
  const [createAdrCertPhoto2, setCreateAdrCertPhoto2] = useState("");

  // booleanos
  const [createCraneHiabLicense, setCreateCraneHiabLicense] = useState("NAO");
  const [createTankerExperience, setCreateTankerExperience] = useState("NAO");
  const [createRefrigeratedExperience, setCreateRefrigeratedExperience] = useState("NAO");
  const [createSpecialTransportExperience, setCreateSpecialTransportExperience] = useState("NAO");
  const [createNightWeekendDriving, setCreateNightWeekendDriving] = useState("NAO");
  const [createSleepInTruck, setCreateSleepInTruck] = useState("NAO");
  const [createUrgentJobs, setCreateUrgentJobs] = useState("NAO");
  const [createLanguagesSpoken, setCreateLanguagesSpoken] = useState("");
  const [createHomeBaseStart, setCreateHomeBaseStart] = useState("");
  const [createHomeBaseEnds, setCreateHomeBaseEnds] = useState("");
  const [createLanesPreference, setCreateLanesPreference] = useState("");

  // NOVO: take_photos
  const [createTakePhotos, setCreateTakePhotos] = useState("NAO");

  // NOVO: gps_sharing
  const [createGpsSharing, setCreateGpsSharing] = useState("NAO");

  // filtros e preferências
  const [filterWaAll, setFilterWaAll] = useState([]); // contém TODOS
  const [filterWaAny, setFilterWaAny] = useState([]); // overlap/ALGUM
  const [filterDistanceMin, setFilterDistanceMin] = useState(""); // km
  const [filterDistanceMax, setFilterDistanceMax] = useState(""); // km
  const [createDistance, setCreateDistance] = useState(""); // km (string numérica)
  const [createWeight, setCreateWeight] = useState(""); // "KG" | "LB"
  const [filterWeight, setFilterWeight] = useState(""); // "KG" | "LB"

  // NOVO: temperatura (create + filtro)
  const [createTemperature, setCreateTemperature] = useState(""); // "Cº" | "Fº"
  const [filterTemperature, setFilterTemperature] = useState(""); // "Cº" | "Fº"

  // NOVO: contact_preferences (create + filtro)
  const [createContactPreferences, setCreateContactPreferences] = useState(""); // enum
  const [filterContactPreferences, setFilterContactPreferences] = useState(""); // enum

  const navigate = useNavigate();

  // reset dos filtros
  const resetWaFilters = () => {
    setFilterWaAll([]);
    setFilterWaAny([]);
  };

  const fetchRows = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const params = {};
      if (filterWaAll.length) params.work_availability = filterWaAll.join(",");
      if (filterWaAny.length) params.work_availability_any = filterWaAny.join(",");
      if (String(filterDistanceMin || "").trim() !== "") params.distance_min = String(filterDistanceMin).trim();
      if (String(filterDistanceMax || "").trim() !== "") params.distance_max = String(filterDistanceMax).trim();
      if (String(filterWeight || "").trim() !== "") params.weight = String(filterWeight).trim();
      if (String(filterTemperature || "").trim() !== "") params.temperature = String(filterTemperature).trim();
      if (String(filterContactPreferences || "").trim() !== "") params.contact_preferences = String(filterContactPreferences).trim();

      const { data } = await axios.get("/driveroriginal", { params });

      const list = Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data)
        ? data
        : [];
      setRows(list);
      if (typeof data?.count === "number") setApiCount(data.count);
      else setApiCount(list.length);
    } catch (_err) {
      setErrorMessage(
        t("errors.load_failed", {
          defaultValue: "Failed to load drivers. Please try again later.",
        })
      );
      setApiCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UX: se ADR = NAO, limpa e desativa validade + fotos ADR
  useEffect(() => {
    if (createAdrHazmarCertification === "NAO") {
      if (createExpiryDateAdrHazmatCertificate) setCreateExpiryDateAdrHazmatCertificate("");
      if (createAdrCertPhoto1) setCreateAdrCertPhoto1("");
      if (createAdrCertPhoto2) setCreateAdrCertPhoto2("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAdrHazmarCertification]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = (r.fullname || "").toLowerCase();
      const nick = (r.nickname || "").toLowerCase();
      const phone = (r.phonenumber || "").toLowerCase();
      const emerg = (r.emergency_name || "").toLowerCase();
      const emergTel = (r.emergency_contact || "").toLowerCase();
      const emergFam = (r.emergency_family || "").toLowerCase();
      const homec = (r.homecity || "").toLowerCase();
      const city2 = (r.city || "").toLowerCase();
      const country = (r.country || "").toLowerCase(); // ISO-2
      const licCtry = (r.license_country || "").toLowerCase(); // full name
      const addr = (r.address || "").toLowerCase();
      const pcode = (r.postal_code || "").toLowerCase();
      const email = (r.email || "").toLowerCase();
      const bdate = (r.birth_date || "").toLowerCase();
      const tax = (r.tax_id || "").toLowerCase();
      const taxExp = (r.tax_id_expiry || "").toLowerCase();
      const dl = (r.driving_license_number || "").toLowerCase();
      const lcats = Array.isArray(r.license_categories) ? r.license_categories.join(", ").toLowerCase() : "";
      const tachoIss = (r.issue_date_tacho_card || "").toLowerCase();
      const tachoExp = (r.expiry_date_tacho_card || "").toLowerCase();
      const workEl = (r.work_eligibility || "").toLowerCase();
      const adrCert = (r.adr_hazmar_certification || "").toLowerCase();
      const adrExp = (r.expiry_date_adr_hazmat_certificate || "").toLowerCase();
      const hiab = (r.crane_hiab_license || "").toLowerCase();
      const tanker = (r.tanker_experience || "").toLowerCase();
      const refrigerated = (r.refrigerated_experience || "").toLowerCase();
      const special = (r.special_transport_experience || "").toLowerCase();
      const nightweek = (r.night_weekend_driving || "").toLowerCase();
      const sleeptruck = (r.sleep_in_truck || "").toLowerCase();
      const urgentjobs = (r.urgent_jobs || "").toLowerCase();
      const langs = Array.isArray(r.languages_spoken) ? r.languages_spoken.join(", ").toLowerCase() : "";
      const hbs = (r.home_base_start || "").toLowerCase();
      const hbe = (r.home_base_ends || "").toLowerCase();
      const wa = Array.isArray(r.work_availability) ? r.work_availability.join(", ").toLowerCase() : "";
      const lanes = (r.lanes_preference || "").toLowerCase();
      const distanceStr = (r.distance == null ? "" : String(r.distance)).toLowerCase();
      const weightStr = (r.weight || "").toLowerCase();
      const tempStr = (r.temperature || "").toLowerCase();
      const contactPrefStr = (r.contact_preferences || "").toLowerCase();
      const takePhotosStr = (r.take_photos || "").toLowerCase();
      const gpsShareStr = (r.gps_sharing || "").toLowerCase();
      const profilePhotoStr = (r.profile_photo || "").toLowerCase();

      return (
        name.includes(q) ||
        nick.includes(q) ||
        phone.includes(q) ||
        profilePhotoStr.includes(q) ||
        emerg.includes(q) ||
        emergTel.includes(q) ||
        emergFam.includes(q) ||
        homec.includes(q) ||
        city2.includes(q) ||
        country.includes(q) ||
        licCtry.includes(q) ||
        addr.includes(q) ||
        pcode.includes(q) ||
        email.includes(q) ||
        bdate.includes(q) ||
        tax.includes(q) ||
        taxExp.includes(q) ||
        dl.includes(q) ||
        lcats.includes(q) ||
        tachoIss.includes(q) ||
        tachoExp.includes(q) ||
        workEl.includes(q) ||
        adrCert.includes(q) ||
        adrExp.includes(q) ||
        hiab.includes(q) ||
        tanker.includes(q) ||
        refrigerated.includes(q) ||
        special.includes(q) ||
        nightweek.includes(q) ||
        sleeptruck.includes(q) ||
        urgentjobs.includes(q) ||
        langs.includes(q) ||
        hbs.includes(q) ||
        hbe.includes(q) ||
        wa.includes(q) ||
        lanes.includes(q) ||
        distanceStr.includes(q) ||
        weightStr.includes(q) ||
        tempStr.includes(q) ||
        contactPrefStr.includes(q) ||
        takePhotosStr.includes(q) ||
        gpsShareStr.includes(q)
      );
    });
  }, [rows, searchTerm]);

  const totalCount = apiCount;
  const visibleCount = filtered.length;

  const handleRemove = async (id) => {
    const ok = window.confirm(
      t("confirm.remove_prompt", {
        defaultValue: "Are you sure you want to remove this driver?",
      })
    );
    if (!ok) return;
    try {
      await axios.delete(`/driveroriginal/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setApiCount((n) => Math.max(0, n - 1));
    } catch (_err) {
      setErrorMessage(
        t("errors.delete_failed", {
          defaultValue: "Failed to delete driver. Please try again later.",
        })
      );
    }
  };

  const resetCreateForm = () => {
    setCreateFullname("");
    setCreateNickname("");
    setCreatePhone("");
    setCreateEmergencyName("");
    setCreateEmergencyContact("");
    setCreateEmergencyFamily("");
    setCreateHomecity("");
    setCreateCity("");
    setCreateCountry("");
    setCreateLicenseCountry("");
    setCreateAddress("");
    setCreatePostalCode("");
    setCreateEmail("");
    setCreateBirthDate("");
    setCreateTaxId("");
    setCreateTaxIdExpiry("");
    setCreateDrivingLicenseNumber("");
    setCreateLicenseCategories("");
    setCreateAcceptterms(false);
    setCreateDLPhoto1("");
    setCreateDLPhoto2("");
    setCreateTachoPhoto1("");
    setCreateTachoPhoto2("");
    setCreateIssueDateTachoCard("");
    setCreateExpiryDateTachoCard("");
    setCreateWorkEligibility("");
    setCreateAdrHazmarCertification("NAO");
    setCreateExpiryDateAdrHazmatCertificate("");
    setCreateAdrCertPhoto1("");
    setCreateAdrCertPhoto2("");
    setCreateCraneHiabLicense("NAO");
    setCreateTankerExperience("NAO");
    setCreateRefrigeratedExperience("NAO");
    setCreateSpecialTransportExperience("NAO");
    setCreateNightWeekendDriving("NAO");
    setCreateSleepInTruck("NAO");
    setCreateUrgentJobs("NAO");
    setCreateLanguagesSpoken("");
    setCreateHomeBaseStart("");
    setCreateHomeBaseEnds("");
    setCreateLanesPreference("");
    setCreateDistance("");
    setCreateWeight("");
    setCreateTemperature("");
    setCreateContactPreferences("");
    setCreateTakePhotos("NAO");
    setCreateGpsSharing("NAO"); // NOVO
  };

  const handleCreateToggle = () => {
    setIsCreating((v) => !v);
    setCreateError("");
    resetCreateForm();
  };

  const handleCreateSave = async () => {
    setCreateError("");
    const name = String(createFullname || "").trim();
    const nickname = String(createNickname || "").trim();
    const phone = String(createPhone || "").trim();
    const emerg = String(createEmergencyName || "").trim();
    const emergTel = String(createEmergencyContact || "").trim();
    const emergFam = String(createEmergencyFamily || "").trim().toUpperCase();
    const homec = String(createHomecity || "").trim();
    const city = String(createCity || "").trim();
    const countryRaw = String(createCountry || "").trim(); // ISO-2
    const licenseCountry = String(createLicenseCountry || "").trim(); // full
    const addr = String(createAddress || "").trim();
    const pcode = String(createPostalCode || "").trim();
    const email = String(createEmail || "").trim();
    const bdate = String(createBirthDate || "").trim();
    const taxRaw = String(createTaxId || "");
    const tax = taxRaw.replace(/\D+/g, "");
    const taxExp = String(createTaxIdExpiry || "").trim();
    const dlNum = String(createDrivingLicenseNumber || "").trim();
    const lcatsParsed = parseLicenseCategoriesInput(createLicenseCategories);
    const issueTacho = String(createIssueDateTachoCard || "").trim();
    const expiryTacho = String(createExpiryDateTachoCard || "").trim();

    // work_eligibility (ENUM 4 opções)
    const workEligibility = String(createWorkEligibility || "").trim().toUpperCase();

    const adrCert = String(createAdrHazmarCertification || "").trim().toUpperCase();
    const adrExpiry = adrCert === "SIM" ? String(createExpiryDateAdrHazmatCertificate || "").trim() : "";

    const hiabCert = String(createCraneHiabLicense || "").trim().toUpperCase();
    const tankerExp = String(createTankerExperience || "").trim().toUpperCase();
    const refrigeratedExp = String(createRefrigeratedExperience || "").trim().toUpperCase();
    const specialTransportExp = String(createSpecialTransportExperience || "").trim().toUpperCase();
    const nightWeekendExp = String(createNightWeekendDriving || "").trim().toUpperCase();
    const sleepInTruck = String(createSleepInTruck || "").trim().toUpperCase();
    const urgentJobs = String(createUrgentJobs || "").trim().toUpperCase();

    const langsParsed = parseLanguagesInput(createLanguagesSpoken);

    const homeBaseStart = String(createHomeBaseStart || "").trim();
    const homeBaseEnds = String(createHomeBaseEnds || "").trim();
    const lanesPreference = String(createLanesPreference || "").trim();

    // validações principais
    if (!name) {
      setCreateError(t("create.fullname_required", { defaultValue: "Full name is required." }));
      return;
    }
    if (nickname && nickname.length > 60) {
      setCreateError(t("create.nickname_len", { defaultValue: "Nickname: máx. 60 caracteres." }));
      return;
    }
    if (email && !EMAIL_RE.test(email)) {
      setCreateError(t("create.email_invalid", { defaultValue: "Invalid email format." }));
      return;
    }
    if (bdate && !DATE_RE.test(bdate)) {
      setCreateError(t("create.birthdate_invalid", { defaultValue: "Birth date must be YYYY-MM-DD." }));
      return;
    }
    if (taxRaw && tax.length !== 9) {
      setCreateError(t("create.taxid_invalid", { defaultValue: "Tax ID (NIF) must have 9 digits." }));
      return;
    }
    if (taxExp && !DATE_RE.test(taxExp)) {
      setCreateError(t("create.taxid_expiry_invalid", { defaultValue: "Tax ID expiry must be YYYY-MM-DD." }));
      return;
    }
    if (countryRaw && !ISO2_RE.test(countryRaw)) {
      setCreateError(t("create.country_invalid", { defaultValue: "Country must be 2 letters (ISO-2), e.g., PT." }));
      return;
    }
    if (emergFam && !EF_OPTIONS.includes(emergFam)) {
      setCreateError(
        t("create.emergency_family_invalid", {
          defaultValue: `Emergency relation must be one of: ${EF_OPTIONS.join(", ")}.`,
        })
      );
      return;
    }
    if (licenseCountry && (licenseCountry.length <= 2 || !/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(licenseCountry))) {
      setCreateError(
        t("create.license_country_invalid", {
          defaultValue: "License country must be the full country name (no abbreviations).",
        })
      );
      return;
    }
    if (lcatsParsed === "__INVALID__") {
      setCreateError(
        t("create.license_categories_invalid", {
          defaultValue: 'License categories must be like "B, C+E" (only A–Z, 0–9 and "+").',
        })
      );
      return;
    }

    // validação home_base_start / home_base_ends (simples)
    const simpleLocOK = (s) => /^[\p{L}\p{M}\s\-\.'&,]*$/u.test(s);
    if (homeBaseStart) {
      if (homeBaseStart.length > 120) {
        setCreateError(t("create.home_base_start_len", { defaultValue: "Home base start: máx. 120 caracteres." }));
        return;
      }
      if (!simpleLocOK(homeBaseStart)) {
        setCreateError(t("create.home_base_start_chars", { defaultValue: "Use apenas letras e símbolos simples (.-'& ,)." }));
        return;
      }
    }
    if (homeBaseEnds) {
      if (homeBaseEnds.length > 120) {
        setCreateError(t("create.home_base_ends_len", { defaultValue: "Home base ends: máx. 120 caracteres." }));
        return;
      }
      if (!simpleLocOK(homeBaseEnds)) {
        setCreateError(t("create.home_base_ends_chars", { defaultValue: "Use apenas letras e símbolos simples (.-'& ,)." }));
        return;
      }
    }
    if (lanesPreference && lanesPreference.length > 255) {
      setCreateError(t("create.lanes_preference_len", { defaultValue: "Lanes preference: máx. 255 caracteres." }));
      return;
    }

    // distance (opcional, inteiro ≥ 0)
    if (createDistance !== "") {
      const n = Number(createDistance);
      if (!Number.isFinite(n) || n < 0) {
        setCreateError(t("create.distance_invalid", { defaultValue: "Distância inválida. Use um número inteiro ≥ 0." }));
        return;
      }
    }
    // weight (opcional: "KG" | "LB")
    const weightSel = String(createWeight || "").trim().toUpperCase();
    if (weightSel && !WEIGHT_OPTIONS.includes(weightSel)) {
      setCreateError('Weight inválido. Use "KG" ou "LB".');
      return;
    }
    // NOVO: temperature (opcional: "Cº" | "Fº")
    const tempSel = String(createTemperature || "").trim();
    if (tempSel && !TEMPERATURE_OPTIONS.includes(tempSel)) {
      setCreateError('Temperature inválida. Use "Cº" ou "Fº".');
      return;
    }
    const contactSel = String(createContactPreferences || "").trim().toUpperCase();
    if (contactSel && !CONTACT_PREF_OPTIONS.includes(contactSel)) {
      setCreateError(`Contact preferences inválido. Use: ${CONTACT_PREF_OPTIONS.join(", ")}.`);
      return;
    }
    // work_eligibility (opcional, mas se vier tem de ser válido)
    if (createWorkEligibility && !WORK_ELIGIBILITY_OPTIONS.includes(workEligibility)) {
      setCreateError(
        t("create.work_eligibility_invalid", {
          defaultValue: `Work eligibility must be one of: ${WORK_ELIGIBILITY_OPTIONS.join(", ")}.`,
        })
      );
      return;
    }

    if (!createAcceptterms) {
      setCreateError(t("create.acceptterms_required", { defaultValue: "You must accept the terms to save." }));
      return;
    }

    try {
      const payload = { fullname: name, acceptterms: true };
      if (nickname) payload.nickname = nickname;
      if (phone) payload.phonenumber = phone;
      if (emerg) payload.emergency_name = emerg;
      if (emergTel) payload.emergency_contact = emergTel;
      if (emergFam) payload.emergency_family = emergFam;
      if (homec) payload.homecity = homec;
      if (city) payload.city = city;
      if (countryRaw) payload.country = countryRaw.toUpperCase();
      if (licenseCountry) payload.license_country = licenseCountry;
      if (addr) payload.address = addr;
      if (pcode) payload.postal_code = pcode;
      if (email) payload.email = email;
      if (bdate) payload.birth_date = bdate;
      if (tax) payload.tax_id = tax;
      if (taxExp) payload.tax_id_expiry = taxExp;
      if (dlNum) payload.driving_license_number = dlNum;
      if (lcatsParsed && Array.isArray(lcatsParsed)) payload.license_categories = lcatsParsed;

      if (issueTacho) payload.issue_date_tacho_card = issueTacho;
      if (expiryTacho) payload.expiry_date_tacho_card = expiryTacho;

      if (workEligibility) payload.work_eligibility = workEligibility;

      payload.adr_hazmar_certification = adrCert || "NAO";
      if (adrCert === "SIM" && adrExpiry) payload.expiry_date_adr_hazmat_certificate = adrExpiry;

      payload.crane_hiab_license = hiabCert || "NAO";
      payload.tanker_experience = tankerExp || "NAO";
      payload.refrigerated_experience = refrigeratedExp || "NAO";
      payload.special_transport_experience = specialTransportExp || "NAO";
      payload.night_weekend_driving = nightWeekendExp || "NAO";
      payload.sleep_in_truck = sleepInTruck || "NAO";
      payload.urgent_jobs = urgentJobs || "NAO";

      // NOVO: take_photos
      payload.take_photos = (createTakePhotos || "NAO");

      // NOVO: gps_sharing
      payload.gps_sharing = (createGpsSharing || "NAO");

      if (langsParsed && Array.isArray(langsParsed)) payload.languages_spoken = langsParsed;
      if (homeBaseStart) payload.home_base_start = homeBaseStart;
      if (homeBaseEnds) payload.home_base_ends = homeBaseEnds;
      if (lanesPreference) payload.lanes_preference = lanesPreference;
      if (createDistance !== "") payload.distance = Number(createDistance);
      if (weightSel) payload.weight = weightSel;
      if (tempSel) payload.temperature = tempSel;
      if (contactSel) payload.contact_preferences = contactSel;

      const { data } = await axios.post("/driveroriginal", payload);
      setRows((prev) => [data, ...prev]);
      setApiCount((n) => n + 1);
      setIsCreating(false);
      resetCreateForm();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (status === 409 && msg?.toLowerCase?.().includes("email")) {
        setCreateError(t("create.email_conflict", { defaultValue: "Email already in use." }));
      } else if (status === 400 && msg) {
        setCreateError(msg);
      } else {
        setCreateError(t("create.failed", { defaultValue: "Could not create driver. Please try again." }));
      }
    }
  };

  if (!ready) {
    return (
      <div className="driverorig-page">
        <section className="driverorig-card driverorig-toolbar">
          <div className="driverorig-toolbar__left">
            <div className="driverorig-stats">
              <span className="driverorig-pill">—</span>
              <span className="driverorig-dot">•</span>
              <span className="driverorig-pill">—</span>
            </div>
          </div>
        </section>
        <section className="driverorig-card">
          <div className="driverorig-table">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="driverorig-row driverorig-row--skeleton driverorig-grid-12" key={`skeleton-${i}`}>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell"><span className="driverorig-skel" /></div>
                <div className="driverorig-cell driverorig-cell--actions"><span className="driverorig-skel small" /></div>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="driverorig-page">
      {/* Header */}
      <section className="driverorig-card driverorig-toolbar">
        <div className="driverorig-toolbar__left">
          <div className="driverorig-stats">
            <span className="driverorig-pill">
              {t("toolbar.total", { defaultValue: "Total" })} <strong>{totalCount}</strong>
            </span>
            <span className="driverorig-dot">•</span>
            <span className="driverorig-pill">
              {t("toolbar.showing", { defaultValue: "Showing" })} <strong>{visibleCount}</strong>
            </span>
          </div>
        </div>

        <div className="driverorig-toolbar__right">
          <div className="driverorig-search">
            <FaSearch className="driverorig-search__icon" />
            <input
              className="driverorig-search__input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("search.placeholder", {
                defaultValue:
                  "Search by name, nickname, phone, emergency (name/phone/relation), license categories, driving license number, license country, home city, HOME BASE START, HOME BASE ENDS, LANES, DISTANCE (km), TEMPERATURE, city, country, address, postal code, email, birth date, tax id or expiry, tacho issue/expiry, work eligibility, ADR, ADR expiry, HIAB, TANKER, REFRIGERATED, SPECIAL, NIGHT/WEEKEND, SLEEP IN TRUCK, URGENT JOBS, TAKE_PHOTOS, GPS_SHARING, LANGUAGES…",
              })}
              aria-label={t("search.aria_label", { defaultValue: "Search drivers" })}
            />
            {searchTerm && (
              <button
                type="button"
                className="driverorig-search__clear"
                onClick={() => setSearchTerm("")}
                aria-label={t("search.clear_aria", { defaultValue: "Clear search" })}
                title={t("search.clear_title", { defaultValue: "Clear" })}
              >
                ✕
              </button>
            )}
          </div>

          {/* Filtros */}
          <div className="driverorig-filters" style={{ marginTop: 8 }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              {/* Contém TODOS */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ opacity: 0.85 }}>Disponibilidade (todos):</strong>
                {WA_DAYS.map((d) => (
                  <button
                    key={`all-${d.code}`}
                    type="button"
                    className={`driverorig-chip ${filterWaAll.includes(d.code) ? "on" : ""}`}
                    onClick={() => toggleDay(filterWaAll, setFilterWaAll, d.code)}
                    title={`Incluir ${d.label} (AND)`}
                    style={{
                      padding: "4px 8px", borderRadius: 8, border: "1px solid var(--loads-border-soft, #2b3340)",
                      background: filterWaAll.includes(d.code) ? "var(--loads-bg-soft,#0f1318)" : "transparent",
                      cursor: "pointer", fontSize: 12
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              {/* Filtro de Weight */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ opacity: 0.85 }}>Peso:</strong>
                <select
                  className="driverorig-create__input"
                  value={filterWeight}
                  onChange={(e) => setFilterWeight(e.target.value)}
                  style={{ width: 110 }}
                >
                  <option value="">Todos</option>
                  {WEIGHT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{WEIGHT_LABELS[opt]}</option>
                  ))}
                </select>
              </div>

              {/* Overlap/ALGUM */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ opacity: 0.85 }}>Disponibilidade (algum):</strong>
                {WA_DAYS.map((d) => (
                  <button
                    key={`any-${d.code}`}
                    type="button"
                    className={`driverorig-chip ${filterWaAny.includes(d.code) ? "on" : ""}`}
                    onClick={() => toggleDay(filterWaAny, setFilterWaAny, d.code)}
                    title={`Incluir ${d.label} (OR)`}
                    style={{
                      padding: "4px 8px", borderRadius: 8, border: "1px solid var(--loads-border-soft, #2b3340)",
                      background: filterWaAny.includes(d.code) ? "var(--loads-bg-soft,#0f1318)" : "transparent",
                      cursor: "pointer", fontSize: 12
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              {/* Filtro de distância (km) */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ opacity: 0.85 }}>Distância (km):</strong>
                <input
                  className="driverorig-create__input"
                  value={filterDistanceMin}
                  onChange={(e) => setFilterDistanceMin(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="mín"
                  inputMode="numeric"
                  style={{ width: 80 }}
                />
                <span>—</span>
                <input
                  className="driverorig-create__input"
                  value={filterDistanceMax}
                  onChange={(e) => setFilterDistanceMax(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="máx"
                  inputMode="numeric"
                  style={{ width: 80 }}
                />
              </div>

              {/* NOVO: filtro Temperature */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ opacity: 0.85 }}>Temperatura:</strong>
                <select
                  className="driverorig-create__input"
                  value={filterTemperature}
                  onChange={(e) => setFilterTemperature(e.target.value)}
                  style={{ width: 110 }}
                >
                  <option value="">Todas</option>
                  {TEMPERATURE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{TEMPERATURE_LABELS[opt]}</option>
                  ))}
                </select>
              </div>

              {/* NOVO: filtro Contact Preferences */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ opacity: 0.85 }}>Contacto:</strong>
                <select
                  className="driverorig-create__input"
                  value={filterContactPreferences}
                  onChange={(e) => setFilterContactPreferences(e.target.value)}
                  style={{ width: 160 }}
                >
                  <option value="">Todos</option>
                  {CONTACT_PREF_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{CONTACT_PREF_LABELS[opt]}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button className="driverorig-btn" type="button" onClick={fetchRows} title="Aplicar filtros">
                  Filtrar
                </button>
                <button
                  className="driverorig-btn"
                  type="button"
                  onClick={() => {
                    resetWaFilters();
                    setFilterDistanceMin("");
                    setFilterDistanceMax("");
                    setFilterWeight("");
                    setFilterTemperature("");
                    setFilterContactPreferences("");
                    fetchRows();
                  }}
                  title="Limpar filtros"
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inline create pill */}
      {isCreating && (
        <section className="driverorig-card driverorig-create">
          <div className="driverorig-create-row driverorig-create-row--11cols">
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.fullname_label", { defaultValue: "Full name" })}</label>
              <input
                className={`driverorig-create__input${createError && !createFullname ? " input-error" : ""}`}
                value={createFullname}
                onChange={(e) => setCreateFullname(e.target.value)}
                placeholder={t("create.fullname_ph", { defaultValue: "e.g., Jane Doe" })}
              />
            </div>

            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.nickname_label", { defaultValue: "Nickname (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createNickname}
                onChange={(e) => setCreateNickname(e.target.value)}
                placeholder={t("create.nickname_ph", { defaultValue: "e.g., João \"Pipas\"" })}
                maxLength={60}
              />
            </div>

            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.phone_label", { defaultValue: "Phone (optional)" })}</label>
              <input
                className="driverorig-create__input"
                value={createPhone}
                onChange={(e) => setCreatePhone(e.target.value)}
                placeholder={t("create.phone_ph", { defaultValue: "+351 912 345 678" })}
              />
            </div>

            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.emergency_name_label", { defaultValue: "Emergency name (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createEmergencyName}
                onChange={(e) => setCreateEmergencyName(e.target.value)}
                placeholder={t("create.emergency_name_ph", { defaultValue: "e.g., Maria Silva" })}
              />
            </div>

            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.emergency_contact_label", { defaultValue: "Emergency phone (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createEmergencyContact}
                onChange={(e) => setCreateEmergencyContact(e.target.value)}
                placeholder={t("create.emergency_contact_ph", { defaultValue: "+351 911 222 333" })}
              />
            </div>

            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.emergency_family_label", { defaultValue: "Emergency relation (optional)" })}
              </label>
              <select
                className="driverorig-create__input"
                value={createEmergencyFamily}
                onChange={(e) => setCreateEmergencyFamily(e.target.value)}
              >
                <option value="">{t("create.emergency_family_ph", { defaultValue: "— select —" })}</option>
                {EF_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {EF_LABELS[opt]}
                  </option>
                ))}
              </select>
            </div>

            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.homecity_label", { defaultValue: "Home city (optional)" })}</label>
              <input
                className="driverorig-create__input"
                value={createHomecity}
                onChange={(e) => setCreateHomecity(e.target.value)}
                placeholder={t("create.homecity_ph", { defaultValue: "e.g., Porto" })}
              />
            </div>

            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.city_label", { defaultValue: "City (optional)" })}</label>
              <input
                className="driverorig-create__input"
                value={createCity}
                onChange={(e) => setCreateCity(e.target.value)}
                placeholder={t("create.city_ph", { defaultValue: "e.g., Lisbon" })}
              />
            </div>

            {/* Country (ISO-2) */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.country_label", { defaultValue: "Country (ISO-2) (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createCountry}
                onChange={(e) => setCreateCountry(e.target.value.toUpperCase())}
                placeholder={t("create.country_ph", { defaultValue: "e.g., PT" })}
                maxLength={2}
              />
            </div>

            {/* License country (full name) */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.license_country_label", { defaultValue: "License country (full name) (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createLicenseCountry}
                onChange={(e) => setCreateLicenseCountry(e.target.value)}
                placeholder={t("create.license_country_ph", { defaultValue: "e.g., Portugal" })}
              />
            </div>

            {/* Address */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.address_label", { defaultValue: "Address (optional)" })}</label>
              <input
                className="driverorig-create__input"
                value={createAddress}
                onChange={(e) => setCreateAddress(e.target.value)}
                placeholder={t("create.address_ph", { defaultValue: "Street, number, etc." })}
              />
            </div>

            {/* Postal code */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.postal_code_label", { defaultValue: "Postal code (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createPostalCode}
                onChange={(e) => setCreatePostalCode(e.target.value)}
                placeholder={t("create.postal_code_ph", { defaultValue: "e.g., 1200-123" })}
              />
            </div>

            {/* Email */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.email_label", { defaultValue: "Email (optional)" })}</label>
              <input
                className="driverorig-create__input"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                placeholder={t("create.email_ph", { defaultValue: "e.g., jane@example.com" })}
                inputMode="email"
              />
            </div>

            {/* Birth date */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.birthdate_label", { defaultValue: "Birth date (optional)" })}</label>
              <input
                className="driverorig-create__input"
                type="date"
                value={createBirthDate}
                onChange={(e) => setCreateBirthDate(e.target.value)}
                placeholder={t("create.birthdate_ph", { defaultValue: "YYYY-MM-DD" })}
                inputMode="numeric"
              />
            </div>

            {/* Driving license number */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.driving_license_label", { defaultValue: "Driving license (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createDrivingLicenseNumber}
                onChange={(e) => setCreateDrivingLicenseNumber(e.target.value)}
                placeholder={t("create.driving_license_ph", { defaultValue: "e.g., 0123456789" })}
              />
            </div>

            {/* License categories */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.license_categories_label", { defaultValue: "License categories (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createLicenseCategories}
                onChange={(e) => setCreateLicenseCategories(e.target.value)}
                placeholder={t("create.license_categories_ph", { defaultValue: "e.g., B, C+E" })}
              />
              <small className="driverorig-hint">
                {t("create.license_categories_hint", { defaultValue: 'Separate with commas; allowed: letters, digits and "+".' })}
              </small>
            </div>

            {/* Driver license photos (até 2) */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.dlphoto_label", { defaultValue: "License photos (up to 2 URLs)" })}
              </label>
              <div className="driverorig-dlphotos">
                <input
                  className="driverorig-create__input"
                  value={createDLPhoto1}
                  onChange={(e) => setCreateDLPhoto1(e.target.value)}
                  placeholder={t("create.dlphoto_ph1", { defaultValue: "Front image URL (optional)" })}
                />
                <input
                  className="driverorig-create__input"
                  value={createDLPhoto2}
                  onChange={(e) => setCreateDLPhoto2(e.target.value)}
                  placeholder={t("create.dlphoto_ph2", { defaultValue: "Back image URL (optional)" })}
                />
              </div>
              <div className="driverorig-dlphotos-preview" style={{ display: "flex", gap: 8, marginTop: 6 }}>
                {createDLPhoto1?.trim() ? (
                  <img
                    src={createDLPhoto1}
                    alt="DL front preview"
                    style={{ height: 36, width: 56, objectFit: "cover", borderRadius: 4, border: "1px solid var(--pd-field-border, #ddd)" }}
                  />
                ) : null}
                {createDLPhoto2?.trim() ? (
                  <img
                    src={createDLPhoto2}
                    alt="DL back preview"
                    style={{ height: 36, width: 56, objectFit: "cover", borderRadius: 4, border: "1px solid var(--pd-field-border, #ddd)" }}
                  />
                ) : null}
              </div>
              <small className="driverorig-hint">
                {t("create.dlphoto_hint", {
                  defaultValue: "Use http(s) URLs ou caminhos válidos (/static, ./, ../). (data: não é suportado pelo backend.)",
                })}
              </small>
            </div>

            {/* Tacho card photos (até 2) */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.tachophoto_label", { defaultValue: "Tachograph card photos (up to 2 URLs)" })}
              </label>
              <div className="driverorig-dlphotos">
                <input
                  className="driverorig-create__input"
                  value={createTachoPhoto1}
                  onChange={(e) => setCreateTachoPhoto1(e.target.value)}
                  placeholder={t("create.tachophoto_ph1", { defaultValue: "Front image URL (optional)" })}
                />
                <input
                  className="driverorig-create__input"
                  value={createTachoPhoto2}
                  onChange={(e) => setCreateTachoPhoto2(e.target.value)}
                  placeholder={t("create.tachophoto_ph2", { defaultValue: "Back image URL (optional)" })}
                />
              </div>
              <div className="driverorig-dlphotos-preview" style={{ display: "flex", gap: 8, marginTop: 6 }}>
                {createTachoPhoto1?.trim() ? (
                  <img
                    src={createTachoPhoto1}
                    alt="Tacho front preview"
                    style={{ height: 36, width: 56, objectFit: "cover", borderRadius: 4, border: "1px solid var(--pd-field-border, #ddd)" }}
                  />
                ) : null}
                {createTachoPhoto2?.trim() ? (
                  <img
                    src={createTachoPhoto2}
                    alt="Tacho back preview"
                    style={{ height: 36, width: 56, objectFit: "cover", borderRadius: 4, border: "1px solid var(--pd-field-border, #ddd)" }}
                  />
                ) : null}
              </div>
              <small className="driverorig-hint">
                {t("create.tachophoto_hint", {
                  defaultValue: "Use http(s) URLs ou caminhos válidos (/static, ./, ../). (data: não é suportado.)",
                })}
              </small>
            </div>

            {/* Issue date tacho */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.issue_tacho_label", { defaultValue: "Tachograph card issue date (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                type="date"
                value={createIssueDateTachoCard}
                onChange={(e) => setCreateIssueDateTachoCard(e.target.value)}
                placeholder="YYYY-MM-DD"
                inputMode="numeric"
              />
            </div>

            {/* Expiry date tacho */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.expiry_tacho_label", { defaultValue: "Tachograph card expiry date (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                type="date"
                value={createExpiryDateTachoCard}
                onChange={(e) => setCreateExpiryDateTachoCard(e.target.value)}
                placeholder="YYYY-MM-DD"
                inputMode="numeric"
              />
            </div>

            {/* Tax ID */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.taxid_label", { defaultValue: "Tax ID (NIF) (optional)" })}</label>
              <input
                className="driverorig-create__input"
                value={createTaxId}
                onChange={(e) => setCreateTaxId(e.target.value)}
                placeholder={t("create.taxid_ph", { defaultValue: "9 digits (e.g., 123456789)" })}
                inputMode="numeric"
              />
            </div>

            {/* Tax ID expiry */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.taxid_expiry_label", { defaultValue: "Tax ID expiry (optional)" })}</label>
              <input
                className="driverorig-create__input"
                type="date"
                value={createTaxIdExpiry}
                onChange={(e) => setCreateTaxIdExpiry(e.target.value)}
                placeholder={t("create.taxid_expiry_ph", { defaultValue: "YYYY-MM-DD" })}
                inputMode="numeric"
              />
            </div>

            {/* Work eligibility (ENUM) */}
            <div className="driverorig-create-field" style={{ gridColumn: "1 / -1" }}>
              <label className="driverorig-create__label">
                {t("create.work_eligibility_label", { defaultValue: "Work eligibility (optional)" })}
              </label>
              <select
                className="driverorig-create__input"
                value={createWorkEligibility}
                onChange={(e) => setCreateWorkEligibility(e.target.value)}
              >
                <option value="">{t("create.work_eligibility_ph", { defaultValue: "— selecionar —" })}</option>
                {WORK_ELIGIBILITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {WORK_ELIGIBILITY_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.work_eligibility_hint", { defaultValue: "Choose among PT, EU/EEA, Work Permit, or None." })}
              </small>
            </div>

            {/* ADR/HazMat Certification */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.adr_label", { defaultValue: "ADR/HazMat certification" })}</label>
              <select
                className="driverorig-create__input"
                value={createAdrHazmarCertification}
                onChange={(e) => setCreateAdrHazmarCertification(e.target.value)}
              >
                {ADR_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {ADR_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.adr_hint", { defaultValue: 'Select "SIM" if the driver holds a valid ADR/HazMat certification.' })}
              </small>
            </div>

            {/* ADR expiry */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.adr_expiry_label", { defaultValue: "ADR/HazMat expiry date (optional)" })}</label>
              <input
                className="driverorig-create__input"
                type="date"
                value={createExpiryDateAdrHazmatCertificate}
                onChange={(e) => setCreateExpiryDateAdrHazmatCertificate(e.target.value)}
                placeholder="YYYY-MM-DD"
                inputMode="numeric"
                disabled={createAdrHazmarCertification !== "SIM"}
              />
            </div>

            {/* ADR certificate photos (até 2) */}
            <div className="driverorig-create-field" style={{ gridColumn: "1 / -1" }}>
              <label className="driverorig-create__label">
                {t("create.adr_cert_photos_label", { defaultValue: "ADR certificate photos (up to 2 URLs)" })}
              </label>
              <div className="driverorig-dlphotos">
                <input
                  className="driverorig-create__input"
                  value={createAdrCertPhoto1}
                  onChange={(e) => setCreateAdrCertPhoto1(e.target.value)}
                  placeholder={t("create.adr_cert_ph1", { defaultValue: "Front image URL (optional)" })}
                  disabled={createAdrHazmarCertification !== "SIM"}
                />
                <input
                  className="driverorig-create__input"
                  value={createAdrCertPhoto2}
                  onChange={(e) => setCreateAdrCertPhoto2(e.target.value)}
                  placeholder={t("create.adr_cert_ph2", { defaultValue: "Back image URL (optional)" })}
                  disabled={createAdrHazmarCertification !== "SIM"}
                />
              </div>
              <div className="driverorig-dlphotos-preview" style={{ display: "flex", gap: 8, marginTop: 6 }}>
                {createAdrCertPhoto1?.trim() ? (
                  <img
                    src={createAdrCertPhoto1}
                    alt="ADR cert front preview"
                    style={{ height: 36, width: 56, objectFit: "cover", borderRadius: 4, border: "1px solid var(--pd-field-border, #ddd)" }}
                  />
                ) : null}
                {createAdrCertPhoto2?.trim() ? (
                  <img
                    src={createAdrCertPhoto2}
                    alt="ADR cert back preview"
                    style={{ height: 36, width: 56, objectFit: "cover", borderRadius: 4, border: "1px solid var(--pd-field-border, #ddd)" }}
                  />
                ) : null}
              </div>
              <small className="driverorig-hint">
                {t("create.adr_cert_hint", {
                  defaultValue: "Use http(s) URLs ou caminhos válidos (/static, ./, ../). (data: não é suportado.)",
                })}
              </small>
            </div>

            {/* Crane/HIAB */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.crane_hiab_label", { defaultValue: "Crane/HIAB license" })}</label>
              <select
                className="driverorig-create__input"
                value={createCraneHiabLicense}
                onChange={(e) => setCreateCraneHiabLicense(e.target.value)}
              >
                {HIAB_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {HIAB_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.crane_hiab_hint", { defaultValue: 'Select "SIM" if the driver is licensed to operate a truck-mounted crane/HIAB.' })}
              </small>
            </div>

            {/* Tanker */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.tanker_label", { defaultValue: "Tanker (cisterna) experience" })}</label>
              <select
                className="driverorig-create__input"
                value={createTankerExperience}
                onChange={(e) => setCreateTankerExperience(e.target.value)}
              >
                {TANKER_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {TANKER_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.tanker_hint", { defaultValue: 'Select "SIM" if the driver has experience operating fuel/chemical tankers.' })}
              </small>
            </div>

            {/* Refrigerated */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.refrigerated_label", { defaultValue: "Refrigerated (frigorífico) experience" })}</label>
              <select
                className="driverorig-create__input"
                value={createRefrigeratedExperience}
                onChange={(e) => setCreateRefrigeratedExperience(e.target.value)}
              >
                {REFRIG_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {REFRIG_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.refrigerated_hint", { defaultValue: 'Select "SIM" if the driver has experience with refrigerated cargo (reefer).' })}
              </small>
            </div>

            {/* Special transport */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.special_transport_label", { defaultValue: "Special transport (excepcional/indivisível)" })}</label>
              <select
                className="driverorig-create__input"
                value={createSpecialTransportExperience}
                onChange={(e) => setCreateSpecialTransportExperience(e.target.value)}
              >
                {SPECIAL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {SPECIAL_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.special_transport_hint", {
                  defaultValue: 'Select "SIM" if the driver has experience with oversize/overweight or exceptional transports.',
                })}
              </small>
            </div>

            {/* Night & Weekend driving */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.night_weekend_label", { defaultValue: "Night & weekend driving" })}</label>
              <select
                className="driverorig-create__input"
                value={createNightWeekendDriving}
                onChange={(e) => setCreateNightWeekendDriving(e.target.value)}
              >
                {NIGHTW_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {NIGHTW_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.night_weekend_hint", { defaultValue: 'Select "SIM" if the driver is available/experienced at night and on weekends.' })}
              </small>
            </div>

            {/* Sleep in Truck */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.sleep_in_truck_label", { defaultValue: "Dormir na cabine (sleep in truck)" })}
              </label>
              <select
                className="driverorig-create__input"
                value={createSleepInTruck}
                onChange={(e) => setCreateSleepInTruck(e.target.value)}
              >
                {SLEEP_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {SLEEP_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.sleep_in_truck_hint", { defaultValue: 'Selecione "SIM" se o(a) motorista está disponível para pernoitar no camião.' })}
              </small>
            </div>

            {/* Urgent jobs */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">{t("create.urgent_jobs_label", { defaultValue: "Trabalhos urgentes (urgent jobs)" })}</label>
              <select
                className="driverorig-create__input"
                value={createUrgentJobs}
                onChange={(e) => setCreateUrgentJobs(e.target.value)}
              >
                {URGENT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {URGENT_LABELS[opt]}
                  </option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.urgent_jobs_hint", { defaultValue: 'Selecione "SIM" se o(a) motorista aceita/está disponível para trabalhos urgentes.' })}
              </small>
            </div>

            {/* NOVO: Take photos */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">Pode tirar fotos (take_photos)</label>
              <select
                className="driverorig-create__input"
                value={createTakePhotos}
                onChange={(e) => setCreateTakePhotos(e.target.value)}
              >
                {TAKE_PHOTOS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{TAKE_PHOTOS_LABELS[opt]}</option>
                ))}
              </select>
              <small className="driverorig-hint">
                O motorista tem autorização/disponibilidade para tirar e enviar fotos (documentos, cargas, ocorrências).
              </small>
            </div>

            {/* Languages spoken */}
            <div className="driverorig-create-field" style={{ gridColumn: "1 / -1" }}>
              <label className="driverorig-create__label">
                {t("create.languages_spoken_label", { defaultValue: "Languages spoken (ISO 639-1, comma separated)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createLanguagesSpoken}
                onChange={(e) => setCreateLanguagesSpoken(e.target.value)}
                placeholder={t("create.languages_spoken_ph", { defaultValue: "pt, en, es" })}
              />
              <small className="driverorig-hint">
                {t("create.languages_spoken_hint", {
                  defaultValue: "Use 2-letter codes (ISO 639-1). Examples: pt, en, es, fr, de, it.",
                })}
              </small>
            </div>

            {/* Home base start */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.home_base_start_label", { defaultValue: "Home base start (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createHomeBaseStart}
                onChange={(e) => setCreateHomeBaseStart(e.target.value)}
                placeholder={t("create.home_base_start_ph", { defaultValue: "e.g., Norte de Portugal / Galicia" })}
                maxLength={120}
              />
              <small className="driverorig-hint">
                {t("create.home_base_start_hint", { defaultValue: "Cidade/região/país de base. Máx. 120; apenas letras e .-'& ," })}
              </small>
            </div>

            {/* Home base ends */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.home_base_ends_label", { defaultValue: "Home base ends (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createHomeBaseEnds}
                onChange={(e) => setCreateHomeBaseEnds(e.target.value)}
                placeholder={t("create.home_base_ends_ph", { defaultValue: "e.g., Centro / Sul de Portugal" })}
                maxLength={120}
              />
              <small className="driverorig-hint">
                {t("create.home_base_ends_hint", { defaultValue: "Zona final do raio de base. Máx. 120; apenas letras e .-'& ," })}
              </small>
            </div>

            {/* Lanes preference (STRING 255) */}
            <div className="driverorig-create-field" style={{ gridColumn: "1 / -1" }}>
              <label className="driverorig-create__label">
                {t("create.lanes_preference_label", { defaultValue: "Lanes preference (optional)" })}
              </label>
              <input
                className="driverorig-create__input"
                value={createLanesPreference}
                onChange={(e) => setCreateLanesPreference(e.target.value)}
                placeholder={t("create.lanes_preference_ph", { defaultValue: "ex.: PT, ES, FR" })}
                maxLength={255}
              />
              <small className="driverorig-hint">
                {t("create.lanes_preference_hint", { defaultValue: "Lista livre separada por vírgulas (máx. 255 caracteres)." })}
              </small>
            </div>

            {/* Distance (km) */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.distance_label", { defaultValue: "Distância (km, opcional)" })}
              </label>
              <input
                className="driverorig-create__input"
                type="number"
                min={0}
                step={1}
                value={createDistance}
                onChange={(e) => setCreateDistance(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="ex.: 100"
                inputMode="numeric"
                maxLength={6}
              />
              <small className="driverorig-hint">
                {t("create.distance_hint", { defaultValue: "Raio preferencial em quilómetros (inteiro ≥ 0)." })}
              </small>
            </div>

            {/* Weight unit (KG | LB) */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                {t("create.weight_label", { defaultValue: "Unidade de peso (opcional)" })}
              </label>
              <select
                className="driverorig-create__input"
                value={createWeight}
                onChange={(e) => setCreateWeight(e.target.value)}
              >
                <option value="">{t("create.weight_ph", { defaultValue: "— selecionar —" })}</option>
                {WEIGHT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{WEIGHT_LABELS[opt]}</option>
                ))}
              </select>
              <small className="driverorig-hint">
                {t("create.weight_hint", { defaultValue: "Escolha entre kg ou lb." })}
              </small>
            </div>

            {/* NOVO: Temperature (Cº | Fº) */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                Temperatura preferida (opcional)
              </label>
              <select
                className="driverorig-create__input"
                value={createTemperature}
                onChange={(e) => setCreateTemperature(e.target.value)}
              >
                <option value="">{t("create.temperature_ph", { defaultValue: "— selecionar —" })}</option>
                {TEMPERATURE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{TEMPERATURE_LABELS[opt]}</option>
                ))}
              </select>
              <small className="driverorig-hint">
                Aceita apenas Cº ou Fº (validação igual ao backend).
              </small>
            </div>

            {/* NOVO: Contact preferences */}
            <div className="driverorig-create-field">
              <label className="driverorig-create__label">
                Preferência de contacto (opcional)
              </label>
              <select
                className="driverorig-create__input"
                value={createContactPreferences}
                onChange={(e) => setCreateContactPreferences(e.target.value)}
              >
                <option value="">{t("create.contact_pref_ph", { defaultValue: "— selecionar —" })}</option>
                {CONTACT_PREF_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{CONTACT_PREF_LABELS[opt]}</option>
                ))}
              </select>
              <small className="driverorig-hint">
                Opções: {CONTACT_PREF_OPTIONS.join(", ")}.
              </small>
            </div>

            {/* NOVO: GPS Sharing (colocado ABAIXO DE TODOS OS FIELDS) */}
            <div className="driverorig-create-field" style={{ gridColumn: "1 / -1" }}>
              <label className="driverorig-create__label">Partilha de GPS em tempo real (gps_sharing)</label>
              <select
                className="driverorig-create__input"
                value={createGpsSharing}
                onChange={(e) => setCreateGpsSharing(e.target.value)}
              >
                {GPS_SHARING_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{GPS_SHARING_LABELS[opt]}</option>
                ))}
              </select>
              <small className="driverorig-hint">
                Autoriza a app a partilhar a localização (GPS) em tempo real para tracking de cargas/entregas.
              </small>
            </div>

            <div className="driverorig-create__actions">
              <label className="driverorig-terms">
                <input type="checkbox" checked={createAcceptterms} onChange={(e) => setCreateAcceptterms(e.target.checked)} />
                <span>{t("create.acceptterms_label", { defaultValue: "I accept the terms" })}</span>
              </label>

              <button className="driverorig-btn" onClick={handleCreateToggle} type="button">
                <FaTimes className="driverorig-btn__icon" />
                {t("create.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                className="driverorig-btn driverorig-btn--primary"
                onClick={handleCreateSave}
                type="button"
                disabled={!createAcceptterms}
                title={
                  !createAcceptterms
                    ? t("create.acceptterms_required_tt", { defaultValue: "Accept the terms to enable Save." })
                    : undefined
                }
              >
                <FaSave className="driverorig-btn__icon" />
                {t("create.save", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
          {createError && <div className="driverorig-create__error">{createError}</div>}
        </section>
      )}

      {/* Error */}
      {errorMessage && (
        <section className="driverorig-card driverorig-error">
          <div className="driverorig-error__text">{errorMessage}</div>
          <button className="driverorig-btn" onClick={fetchRows}>
            {t("errors.try_again", { defaultValue: "Try again" })}
          </button>
        </section>
      )}

      {/* Table */}
      <section className="driverorig-card">
        <div className="driverorig-table">
          {/* 12 colunas */}
          <div className="driverorig-table__header driverorig-grid-12">
            <div>{t("table.columns.fullname", { defaultValue: "Full name" })}</div>
            <div>{t("table.columns.phone_emergency", { defaultValue: "Phone • Emergency" })}</div>
            <div>{t("table.columns.homecity", { defaultValue: "Home city" })}</div>
            <div>{t("table.columns.city", { defaultValue: "City" })}</div>
            <div>{t("table.columns.country", { defaultValue: "Country (ISO-2) • License country" })}</div>
            <div>{t("table.columns.address", { defaultValue: "Address" })}</div>
            <div>{t("table.columns.postal_code", { defaultValue: "Postal code" })}</div>
            <div>{t("table.columns.email", { defaultValue: "Email" })}</div>
            <div>{t("table.columns.birthdate", { defaultValue: "Birth date" })}</div>
            <div>{t("table.columns.taxid", { defaultValue: "Tax ID (NIF)" })}</div>
            <div>{t("table.columns.taxid_expiry", { defaultValue: "Tax ID expiry" })}</div>
            <div className="driverorig-col-actions">{t("table.columns.actions", { defaultValue: "Actions" })}</div>
          </div>

          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div className="driverorig-row driverorig-row--skeleton driverorig-grid-12" key={`skeleton-${i}`}>
                {Array.from({ length: 11 }).map((__, j) => (
                  <div className="driverorig-cell" key={`skel-${i}-${j}`}>
                    <span className="driverorig-skel" />
                  </div>
                ))}
                <div className="driverorig-cell driverorig-cell--actions">
                  <span className="driverorig-skel small" />
                </div>
              </div>
            ))
          ) : visibleCount > 0 ? (
            filtered.map((r) => (
              <div
                key={r.id}
                className="driverorig-row driverorig-grid-12"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/driveroriginal/${r.id}`)}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/driveroriginal/${r.id}`)}
                title={r.fullname}
              >
                <div className="driverorig-cell" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "1px solid var(--loads-border-soft, #2b3340)",
                      background: "var(--loads-bg-soft,#0f1318)",
                      flex: "0 0 34px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title={r.profile_photo ? "Profile photo" : "No profile photo"}
                  >
                    {r.profile_photo ? (
                      <img
                        src={r.profile_photo}
                        alt="profile"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <span style={{ fontSize: 12, opacity: 0.7 }}>
                        {(r.fullname || "?").trim().slice(0, 1).toUpperCase()}
                      </span>
                    )}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.fullname}
                      {r.nickname ? ` • “${r.nickname}”` : ""}
                    </div>
                    {r.profile_photo ? (
                      <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>
                        profile_photo ✓
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="driverorig-cell">
                  {r.phonenumber || "—"}
                  {r.emergency_name ? ` • ${r.emergency_name}` : ""}
                  {r.emergency_contact ? ` • ${r.emergency_contact}` : ""}
                  {r.emergency_family ? ` • (${EF_LABELS[r.emergency_family] || r.emergency_family})` : ""}
                  {r.driving_license_number ? ` • DL: ${r.driving_license_number}` : ""}
                  {Array.isArray(r.license_categories) && r.license_categories.length > 0 ? ` • Cat: ${r.license_categories.join(", ")}` : ""}
                  {Array.isArray(r.driver_license_photo) && r.driver_license_photo.length > 0 ? (
                    <>
                      {" "}
                      • <FaIdCard style={{ verticalAlign: "text-bottom" }} />
                      <span title="License photos"> {r.driver_license_photo.length}/2</span>
                    </>
                  ) : (
                    ""
                  )}
                  {Array.isArray(r.tacho_card_photo) && r.tacho_card_photo.length > 0 ? (
                    <>
                      {" "}
                      • <FaIdCard style={{ verticalAlign: "text-bottom" }} />
                      <span title="Tachograph card photos"> {r.tacho_card_photo.length}/2</span>
                    </>
                  ) : (
                    ""
                  )}
                  {Array.isArray(r.upload_photo_adr_hazmat_certificate) && r.upload_photo_adr_hazmat_certificate.length > 0 ? (
                    <>
                      {" "}
                      • <FaIdCard style={{ verticalAlign: "text-bottom" }} />
                      <span title="ADR certificate photos"> {r.upload_photo_adr_hazmat_certificate.length}/2</span>
                    </>
                  ) : (
                    ""
                  )}
                  {Array.isArray(r.work_availability) && r.work_availability.length > 0 ? ` • WA: ${r.work_availability.join(",")}` : ""}
                  {r.issue_date_tacho_card ? ` • Tacho issued: ${r.issue_date_tacho_card}` : ""}
                  {r.expiry_date_tacho_card ? ` • Tacho expiry: ${r.expiry_date_tacho_card}` : ""}
                  {r.work_eligibility ? ` • Work: ${r.work_eligibility}` : ""}
                  {r.adr_hazmar_certification ? ` • ADR: ${r.adr_hazmar_certification}` : ""}
                  {r.expiry_date_adr_hazmat_certificate ? ` • ADR exp: ${r.expiry_date_adr_hazmat_certificate}` : ""}
                  {r.crane_hiab_license ? ` • HIAB: ${r.crane_hiab_license}` : ""}
                  {r.tanker_experience ? ` • TANKER: ${r.tanker_experience}` : ""}
                  {r.refrigerated_experience ? ` • REFRIGERATED: ${r.refrigerated_experience}` : ""}
                  {r.special_transport_experience ? ` • SPECIAL: ${r.special_transport_experience}` : ""}
                  {r.night_weekend_driving ? ` • NIGHT/WEEKEND: ${r.night_weekend_driving}` : ""}
                  {r.sleep_in_truck ? ` • SLEEP-IN-TRUCK: ${r.sleep_in_truck}` : ""}
                  {r.urgent_jobs ? ` • URGENT: ${r.urgent_jobs}` : ""}
                  {r.take_photos ? ` • TAKE_PHOTOS: ${r.take_photos}` : ""} 
                  {r.gps_sharing ? ` • GPS: ${r.gps_sharing}` : ""} 
                  {Array.isArray(r.languages_spoken) && r.languages_spoken.length > 0 ? ` • Lang: ${r.languages_spoken.join(", ")}` : ""}
                </div>

                <div className="driverorig-cell">
                  {r.homecity || "—"}
                  {r.home_base_start ? ` • HBS: ${r.home_base_start}` : ""}
                  {r.home_base_ends ? ` • HBE: ${r.home_base_ends}` : ""}
                  {r.lanes_preference ? ` • LANES: ${r.lanes_preference}` : ""}
                  {Number.isFinite(Number(r.distance)) ? ` • DIST: ${r.distance} km` : ""}
                  {r.weight ? ` • WT: ${r.weight}` : ""}
                  {r.temperature ? ` • TEMP: ${r.temperature}` : ""}
                  {r.contact_preferences ? ` • CONTACT: ${CONTACT_PREF_LABELS[r.contact_preferences] || r.contact_preferences}` : ""}
                </div>

                <div className="driverorig-cell">{r.city || "—"}</div>
                <div className="driverorig-cell">
                  {r.country || "—"}
                  {r.license_country ? ` • ${r.license_country}` : ""}
                </div>
                <div className="driverorig-cell">{r.address || "—"}</div>
                <div className="driverorig-cell">{r.postal_code || "—"}</div>
                <div className="driverorig-cell">{r.email || "—"}</div>
                <div className="driverorig-cell">{r.birth_date || "—"}</div>
                <div className="driverorig-cell">{r.tax_id || "—"}</div>
                <div className="driverorig-cell">{r.tax_id_expiry || "—"}</div>
                <div className="driverorig-cell driverorig-cell--actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="driverorig-icon-btn danger"
                    title={t("table.remove.title", { defaultValue: "Remove" })}
                    onClick={() => handleRemove(r.id)}
                    aria-label={t("table.remove.aria", { defaultValue: "Remove driver" })}
                    type="button"
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="driverorig-empty">
              <div className="driverorig-empty__title">{t("empty.title", { defaultValue: "No drivers yet" })}</div>
              <div className="driverorig-empty__subtitle">
                {t("empty.subtitle", { defaultValue: "Create your first driver to get started." })}
              </div>
              <button className="driverorig-btn driverorig-btn--primary" onClick={handleCreateToggle} type="button">
                <FaPlus className="driverorig-btn__icon" />
                {t("empty.add_driver", { defaultValue: "Add driver" })}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default DriverOriginal;
